import { Optional, ResumeSkill } from 'packages/types'
import scrollParent from 'scrollparent'
import { Step } from 'react-joyride'
import {
  AutoTailoringMode,
  AutoTailoringFromPublicJobBoardStep,
  PossibleAutoTailoringUrlParams,
  ResumeSectionId,
} from './types'

export function getCardHighlightId<T extends ResumeSectionId>(
  sectionName: T,
  cardId: Optional<number> | string,
): string {
  return `${sectionName}.${cardId}`
}

export function getCardFieldHighlightId<T extends ResumeSectionId>(
  sectionName: T,
  cardId: Optional<number> | string,
  // Ideally here we should put computed style here, but it breaks on the fact
  // that we have custom sections with numbers as IDs
  fieldName: string,
): string {
  return `${getCardHighlightId(sectionName, cardId)}.${fieldName}`
}

/**
 * This fix may help if you open modal with scrollable content over scrollable page.
 * In this case react-joyride will add extra value to top position of spotlight,
 * and there is no other way to avoid this
 * @see https://github.com/gilbarbara/react-joyride/blob/c6c7bd7b4a6f68e8ba009083d825673b47196cb2/src/modules/dom.ts#L209
 */
export const joyRideSpotlightPositionInModalCssFix = (targetElement: Optional<HTMLElement>) => {
  let mobilePositionTopFix: Step['styles'] | undefined
  if (targetElement && document.scrollingElement) {
    const parent = scrollParent(targetElement)
    if (parent && parent.scrollHeight > parent.offsetHeight) {
      mobilePositionTopFix = {
        spotlight: {
          marginTop: -document.scrollingElement.scrollTop,
        },
      }
    }
  }

  return mobilePositionTopFix
}

export const getSkillsByCategoryId = (
  items: ResumeSkill[],
  categoryId: Optional<number | string>,
) => {
  if (!categoryId) return []

  return items
    .filter(el => el.skillCategoryId === categoryId)
    .sort((a, b) => (a.positionInCategory ?? 0) - (b.positionInCategory ?? 0))
}

export const createAutoTailoringUrlParams = (
  params: PossibleAutoTailoringUrlParams,
): URLSearchParams => {
  const {
    mode,
    jobCardId,
    resumeId,
    jobPostingId,
    autotailoringStep,
    approveAutoTailoredResume,
    showSelfTailorModal,
    preventUrlParamsCleanup,
    showResumeTailoredBanner,
  } = params

  return new URLSearchParams({
    mode,
    ...(jobCardId && { card_id: String(jobCardId) }),
    ...(resumeId && { resume_id: String(resumeId) }),
    ...(jobPostingId && { job_posting_id: String(jobPostingId) }),
    ...(autotailoringStep && { step: autotailoringStep }),
    ...(approveAutoTailoredResume && {
      approve_auto_tailored_resume: String(approveAutoTailoredResume),
    }),
    ...(showSelfTailorModal && { show_self_tailor_modal: String(showSelfTailorModal) }),
    ...(preventUrlParamsCleanup && { prevent_url_params_cleanup: String(preventUrlParamsCleanup) }),
    ...(showResumeTailoredBanner && {
      show_resume_tailored_banner: String(showResumeTailoredBanner),
    }),
  })
}

export const parseAutoTailoringUrlParams = (url: string): PossibleAutoTailoringUrlParams => {
  const urlParams = new URLSearchParams(url)
  return {
    mode: urlParams.get('mode') as AutoTailoringMode,
    jobCardId: Number(urlParams.get('card_id')),
    resumeId: Number(urlParams.get('resume_id')),
    jobPostingId: Number(urlParams.get('job_posting_id')),
    autotailoringStep: urlParams.get('step') as AutoTailoringFromPublicJobBoardStep,
    approveAutoTailoredResume: urlParams.get('approve_auto_tailored_resume') === 'true',
    showSelfTailorModal: urlParams.get('show_self_tailor_modal') === 'true',
    preventUrlParamsCleanup: urlParams.get('prevent_url_params_cleanup') === 'true',
    showResumeTailoredBanner: urlParams.get('show_resume_tailored_banner') === 'true',
  }
}
